import React, { useState, useEffect, useCallback } from 'react';
import {
  FiDatabase, FiRefreshCw, FiSave, FiEye, FiEyeOff,
  FiCheck, FiAlertCircle, FiWifi, FiWifiOff, FiExternalLink,
} from 'react-icons/fi';
import {
  getAdminAzureStorageConfig, updateAdminAzureStorageConfig, testAdminAzureStorageConfig,
} from '../../api/admin';
import type { AdminAzureStorageConfig } from '../../api/admin';

/* ── Badge dernier test ───────────────────────────────────────────────────── */
const TestBadge: React.FC<{ status: 'ok' | 'error' | null; testedAt: string | null }> = ({ status, testedAt }) => {
  if (!status || !testedAt) return null;
  const date = new Date(testedAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  return status === 'ok' ? (
    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
      <FiWifi size={11} /> Dernier test OK — {date}
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
      <FiWifiOff size={11} /> Dernier test échoué — {date}
    </span>
  );
};

/* ── Composant principal ──────────────────────────────────────────────────── */
const AdminAzureStoragePage: React.FC = () => {
  const [config,  setConfig]  = useState<AdminAzureStorageConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  // Formulaire
  const [accountName,            setAccountName]            = useState('');
  const [accountKey,             setAccountKey]             = useState('');
  const [architecturesContainer, setArchitecturesContainer] = useState('');
  const [bundlesContainer,       setBundlesContainer]       = useState('');
  const [tfstateResourceGroup,   setTfstateResourceGroup]   = useState('');
  const [tfstateContainer,       setTfstateContainer]       = useState('');
  const [isActive,               setIsActive]               = useState(false);

  // Test
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { config: data } = await getAdminAzureStorageConfig();
      if (data) {
        setConfig(data);
        setAccountName(data.account_name || '');
        setArchitecturesContainer(data.architectures_container || 'architectures');
        setBundlesContainer(data.bundles_container || 'execution-bundles');
        setTfstateResourceGroup(data.tfstate_resource_group || '');
        setTfstateContainer(data.tfstate_container || 'tfstate');
        setIsActive(data.is_active);
      }
    } catch { setError('Impossible de charger la configuration Azure Storage'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      const payload: any = {
        account_name:            accountName,
        architectures_container: architecturesContainer,
        bundles_container:       bundlesContainer,
        tfstate_resource_group:  tfstateResourceGroup,
        tfstate_container:       tfstateContainer,
        is_active:               isActive,
      };
      if (accountKey.trim()) payload.account_key = accountKey.trim();
      const { config: updated } = await updateAdminAzureStorageConfig(payload);
      setConfig(updated);
      setAccountKey('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError('Échec de la sauvegarde'); }
    finally  { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const result = await testAdminAzureStorageConfig();
      setTestResult(result);
      setConfig(c => c ? {
        ...c,
        test_status: result.success ? 'ok' : 'error',
        last_tested_at: new Date().toISOString(),
      } : c);
    } catch { setTestResult({ success: false, message: 'Erreur réseau' }); }
    finally  { setTesting(false); }
  };

  const isConfigured = !!(config?.account_name && config?.has_account_key);
  const portalUrl = accountName
    ? `https://portal.azure.com/#blade/Microsoft_Azure_Storage/StorageMenuBlade/overview/storageAccountId/%2Fsubscriptions%2F.%2FresourceGroups%2F.%2Fproviders%2FMicrosoft.Storage%2FstorageAccounts%2F${accountName}`
    : 'https://portal.azure.com/#browse/Microsoft.Storage%2FStorageAccounts';

  return (
    <div className="p-6 max-w-5xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiDatabase className="text-blue-600" /> Azure Storage
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Stockage des architectures, bundles Terraform et états Terraform
          </p>
        </div>
        <div className="flex gap-2">
          {config?.has_account_key && (
            <button onClick={runTest} disabled={testing}
              className="flex items-center gap-2 px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm hover:bg-blue-50 transition disabled:opacity-50">
              {testing
                ? <FiRefreshCw size={14} className="animate-spin" />
                : <FiWifi size={14} />}
              Tester la connexion
            </button>
          )}
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      {/* ── Statut global ── */}
      {!loading && config && (
        <div className={`mb-5 p-3 rounded-xl border flex items-center gap-3 ${
          isConfigured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}>
          {isConfigured ? (
            <>
              <FiCheck className="text-green-600 flex-shrink-0" size={18} />
              <div className="flex-1 flex items-center gap-3 flex-wrap">
                <p className="text-sm font-medium text-green-800">
                  Azure Storage configuré — <strong>{config.account_name}</strong>
                </p>
                <TestBadge status={config.test_status} testedAt={config.last_tested_at} />
              </div>
              <a href={portalUrl} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline flex-shrink-0">
                Portail Azure <FiExternalLink size={11} />
              </a>
            </>
          ) : (
            <>
              <FiAlertCircle className="text-orange-600 flex-shrink-0" size={18} />
              <p className="text-sm font-medium text-orange-800">
                Configuration Azure Storage incomplète — le stockage ne fonctionnera pas
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Résultat test ── */}
      {testResult && (
        <div className={`mb-4 p-3 rounded-xl border flex items-center gap-2 text-sm ${
          testResult.success
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {testResult.success ? <FiWifi size={15} /> : <FiWifiOff size={15} />}
          {testResult.message}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">Chargement…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── Colonne gauche (2/3) ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Storage Account */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900 text-sm">Storage Account</h3>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom du compte</label>
                  <input type="text" value={accountName} onChange={e => setAccountName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="moncomptestorage" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Clé de compte (Account Key)
                    {config?.has_account_key && (
                      <span className="ml-2 text-green-600 font-normal text-xs">
                        <FiCheck size={11} className="inline" /> déjà configurée — laisser vide pour conserver
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type={showKey ? 'text' : 'password'}
                      value={accountKey}
                      onChange={e => setAccountKey(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder={config?.has_account_key ? '••••••••••••••••' : 'Clé base64 Azure Storage'}
                    />
                    <button type="button" onClick={() => setShowKey(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showKey ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Portail Azure → Storage Account → <strong>Clés d'accès</strong> → key1 ou key2
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="storage-active" checked={isActive} onChange={e => setIsActive(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                  <label htmlFor="storage-active" className="text-sm text-gray-700">Activer Azure Storage</label>
                </div>
              </div>
            </div>

            {/* Containers */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900 text-sm">Conteneurs Blob</h3>
              </div>
              <div className="px-5 py-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Architectures</label>
                  <input type="text" value={architecturesContainer} onChange={e => setArchitecturesContainer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="architectures" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bundles d'exécution</label>
                  <input type="text" value={bundlesContainer} onChange={e => setBundlesContainer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="execution-bundles" />
                </div>
              </div>
            </div>

            {/* Terraform State */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900 text-sm">Terraform State Backend</h3>
              </div>
              <div className="px-5 py-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Resource Group (tfstate)</label>
                  <input type="text" value={tfstateResourceGroup} onChange={e => setTfstateResourceGroup(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="rg-geniac-tfstate" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Conteneur tfstate</label>
                  <input type="text" value={tfstateContainer} onChange={e => setTfstateContainer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="tfstate" />
                </div>
              </div>
            </div>

            {/* Sauvegarder */}
            <div className="flex items-center justify-between">
              {success
                ? <span className="text-sm text-green-600 flex items-center gap-1"><FiCheck size={14} /> Sauvegardé</span>
                : <span />
              }
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition disabled:opacity-50">
                {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
                Enregistrer
              </button>
            </div>
          </div>

          {/* ── Colonne droite (1/3) ── */}
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                <FiAlertCircle size={14} className="text-gray-500" /> Architecture des conteneurs
              </h4>
              <ul className="text-xs text-gray-600 space-y-2">
                <li>
                  <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">architectures/</code>
                  <span className="ml-2 text-gray-500">fichiers JSON des architectures</span>
                </li>
                <li>
                  <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">execution-bundles/</code>
                  <span className="ml-2 text-gray-500">zips Terraform générés</span>
                </li>
                <li>
                  <code className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono">tfstate/</code>
                  <span className="ml-2 text-gray-500">états Terraform remote</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-xs text-blue-800 space-y-2">
              <p className="font-medium">Chiffrement AES-256</p>
              <p>La clé de compte est chiffrée avant d'être stockée. Les modifications s'appliquent immédiatement sans redémarrage.</p>
            </div>

            <a href={portalUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition w-full">
              <FiExternalLink size={14} className="text-blue-500" />
              Ouvrir dans le Portail Azure
            </a>
          </div>

        </div>
      )}
    </div>
  );
};

export default AdminAzureStoragePage;
